import {
  NewProductModal,
  ProductDetailsModal,
} from "../../interfaces/ProductObject";
import { UserPreferences, UserProfile, ProfileSearchApiResponse } from "../../interfaces/UserObject";
import ProductConvertor from "../converters/uiToBackend/ProductConvertor";
import UserConvertor from "../converters/uiToBackend/UserConvertor";
import SearchConvertor from "../converters/uiToBackend/SearchConvertor";
import { postRequest, putRequest, getRequest, deleteRequest, getPublicAsset } from "../http/httpRequest";
import { getUserAvatarUrl } from "../helpers/ImageUrlHelper";
import { AppConstants } from "../../constants/AppConstants";

async function sendOtp(phoneNumber: String): Promise<String> {
  const endpoint = "/api/v1/otp/send-phone-otp?phoneNumber=" + phoneNumber;
  const includeBearerToken = false;
  const response = await postRequest(endpoint, {}, includeBearerToken);
  const data = await response.json();

  return data;
}

async function verifyCorporateEmailOTP(payload: any): Promise<String> {
  const endpoint = "/api/v1/user/make-me-trusted";
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  return data;
}

async function signUp(payload: any): Promise<any> {
  const endpoint = "/api/v1/user/signup";
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();

  return data;
}

async function login(payload: any): Promise<any> {
  const endpoint = "/api/v1/user/login";
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();

  return data;
}

async function forgotPassword(payload: any) {
  const endpoint = "/api/v1/user/reset-password";
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();

  return data;
}

async function updateProfile(profileDb: UserProfile): Promise<any> {
  const endpoint = "/api/v1/user/update-profile";
  const payload = UserConvertor.buildUpdateProfilePayload(profileDb);
  const includeBearerToken = true;
  const response = await putRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  return data;
}

// Deletes the currently authenticated user's account. The backend identifies
// the user from the Bearer token, so no payload is required.
async function deleteProfile(): Promise<any> {
  const endpoint = "/api/v1/user/delete-profile";
  const includeBearerToken = true;
  const response = await deleteRequest(endpoint, includeBearerToken);
  const data = await response.json();
  return data;
}

// Removes this device's Firebase Cloud Messaging token from the current user
// (pruned from profiles.fcmTokens). Called on logout / account deletion so a
// shared device stops receiving the previous user's pushes.
async function deregisterUserFcmToken(token: string): Promise<any> {
  const endpoint = "/api/v1/profile/deregisterFcmToken";
  const payload = { token };
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  // The endpoint may return an empty body; guard the JSON parse.
  try {
    return await response.json();
  } catch {
    return { ok: response.ok };
  }
}

async function validateEmail(payload: any): Promise<any> {
  const endpoint = "/api/v1/otp/send-email-otp?email=" + payload;
  const response = await postRequest(endpoint, {}, true);

  const data = await response.json();
  if (response.status !== AppConstants.HTTP_STATUS.OK) {
    throw new Error(data.contents[0]);
  }
  return data;
}

async function getprofileById(): Promise<any> {
  const endpoint = "/api/v1/user/getprofilebyid";
  const response = await getRequest(endpoint, true);
  const data = await response.json();
  return data;
}

async function getOtherUserDetailsByUserId(otherUserId: string): Promise<any> {
  const endpoint = `/api/v1/user/getotheruserdetailsbyuserid`;
  const includeBearerToken = true;
  const payload = { otherUserId };
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  return data;
}

async function getProfileByPhone(phoneNumber: string): Promise<any> {
  const endpoint = "/api/v1/user/getProfileByPhone";
  const payload = { phoneNumber };
  const response = await postRequest(endpoint, payload, true);
  const data = await response.json();
  return data;
}

async function updateUserPreferences(userPreferences: UserPreferences): Promise<any> {
  const endpoint = "/api/v1/user/update-preferences";
  const payload = UserConvertor.buildUpdateUserPreferencesPayload(userPreferences);
  const response = await postRequest(endpoint, payload, true);
  const data = await response.json();
  return data;
}

async function uploadProfilePicture(
  imageData: FormData,
  userId: string
): Promise<any> {
  // @TODO: TEMPORARY WORKAROUND - Using product images endpoint for testing
  // @TODO: Change to proper endpoint once backend is implemented:
  // const endpoint = `/api/v1/user/${userId}/profile-picture`;
  const endpoint = `/api/v1/products/${userId}/profile-picture`;
  const includeBearerToken = true;
  const response = await postRequest(endpoint, imageData, includeBearerToken);
  return response.json();
}

// Search profiles (people) by query + location. Reuses the shared search
// payload (see SearchConvertor) so business search can reuse it in future.
async function profileSearch(
  searchString: string,
  userPreferences: UserPreferences
): Promise<ProfileSearchApiResponse> {
  const endpoint = "/api/v1/user/profile-search";
  const payload = SearchConvertor.buildSearchPayload(
    searchString,
    userPreferences
  );
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  return data;
}

async function getProfilePicture(userId: string): Promise<string | null> {
  const imageUrl = getUserAvatarUrl(userId);
  try {
    const response = await getPublicAsset(imageUrl);
    if (response && response.ok) {
      return imageUrl;
    }
    return null;
  } catch (error) {
    return null;
  }
}

/** Resolves a user's display name by id; null if not found. Centralizes the response-shape assumption. */
async function getUserNameById(userId: string): Promise<string | null> {
  const data: any = await getOtherUserDetailsByUserId(userId);
  return data?.contents?.[0]?.userName ?? data?.contents?.[0]?.name ?? null;
}

/** Shape this endpoint actually returns for the fields callers below read. */
interface OtherUserDetailsResponse {
  contents?: { phone?: string }[];
  phone?: string;
}

/**
 * Resolves a user's phone number by id; null if not found. Single place that
 * knows this endpoint's response shape (`contents[0].phone`, with a bare
 * `.phone` fallback) — callers (useSellerContact, useUserPhoneById) both read
 * this instead of re-parsing the raw response themselves.
 */
async function getUserPhoneById(userId: string): Promise<string | null> {
  const data = (await getOtherUserDetailsByUserId(userId)) as OtherUserDetailsResponse;
  const phone = data?.contents?.[0]?.phone ?? data?.phone ?? "";
  return phone || null;
}

export default {
  sendOtp,
  verifyCorporateEmailOTP,
  signUp,
  login,
  updateProfile,
  deleteProfile,
  deregisterUserFcmToken,
  validateEmail,
  getprofileById,
  getOtherUserDetailsByUserId,
  getUserNameById,
  getUserPhoneById,
  getProfileByPhone,
  forgotPassword,
  updateUserPreferences,
  uploadProfilePicture,
  getProfilePicture,
  profileSearch,
};
